import { useState } from "react";
import type { CampaignDraftData } from "../page";

interface Props {
  onBack: () => void;
  onNext: () => void;
  draft: CampaignDraftData;
  onUpdateDraft: (data: Partial<CampaignDraftData>) => void;
}

export default function StepSchedule({ onBack, onNext, draft, onUpdateDraft }: Props) {
  const [sendOption, setSendOption] = useState<"now" | "later">(draft.sendOption || "now");
  const [scheduleDate, setScheduleDate] = useState(draft.scheduleDate || "");
  const [scheduleTime, setScheduleTime] = useState(draft.scheduleTime || "10:00");

  const handleSelectOption = (opt: "now" | "later") => {
    setSendOption(opt);
    onUpdateDraft({ sendOption: opt });
  };

  const handleNext = () => {
    onUpdateDraft({
      sendOption,
      scheduleDate,
      scheduleTime,
    });
    onNext();
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4">When to send?</h2>

        <div className="space-y-3 mb-5">
          {/* Send Now */}
          <button
            onClick={() => handleSelectOption("now")}
            className={`w-full text-left p-4 rounded-2xl border transition-all cursor-pointer ${
              sendOption === "now" ? "border-2 border-indigo-600 bg-indigo-50/70 shadow-sm" : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-flashlight-line text-indigo-600 text-lg" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Send Now</p>
                <p className="text-xs text-gray-500">Starts broadcasting immediately after review</p>
              </div>
            </div>
          </button>

          {/* Schedule for later */}
          <button
            onClick={() => handleSelectOption("later")}
            className={`w-full text-left p-4 rounded-2xl border transition-all cursor-pointer ${
              sendOption === "later" ? "border-2 border-indigo-600 bg-indigo-50/70 shadow-sm" : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-line text-amber-600 text-lg" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Schedule for later</p>
                <p className="text-xs text-gray-500">Pick a specific date and time</p>
              </div>
            </div>
            {sendOption === "later" && (
              <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-indigo-100" onClick={(e) => e.stopPropagation()}>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => {
                      setScheduleDate(e.target.value);
                      onUpdateDraft({ scheduleDate: e.target.value });
                    }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Time</label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => {
                      setScheduleTime(e.target.value);
                      onUpdateDraft({ scheduleTime: e.target.value });
                    }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          ← Back
        </button>
        <button
          onClick={handleNext}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-sm"
        >
          Next: Review <i className="ri-arrow-right-line" />
        </button>
      </div>
    </div>
  );
}